import { useState, useEffect } from 'react';
import api from '../services/api';

// Defaults seguros: ningún feature flag se considera activo hasta confirmación del backend.
const DEFAULT_FEATURES = {};
const INVALIDATE_EVENT = 'entitlements-invalidate';

let cachedEntitlements = null;

/** Limpia cache al cambiar de usuario/sesión (login, logout, impersonación). */
export function clearEntitlementsCache() {
  cachedEntitlements = null;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(INVALIDATE_EVENT));
  }
}

export const useEntitlements = () => {
  const [entitlements, setEntitlements] = useState(cachedEntitlements || { features: DEFAULT_FEATURES });
  const [loading, setLoading] = useState(!cachedEntitlements);

  useEffect(() => {
    let isMounted = true;
    const fetchEntitlements = async () => {
      if (isMounted) setLoading(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          cachedEntitlements = null;
          if (isMounted) {
            setEntitlements({ features: DEFAULT_FEATURES });
            setLoading(false);
          }
          return;
        }
        const res = await api.get('/entitlements/current');
        const nextEntitlements = res.data && res.data.features ? res.data : { features: DEFAULT_FEATURES };
        cachedEntitlements = nextEntitlements;
        if (isMounted) {
          setEntitlements(nextEntitlements);
          setLoading(false);
        }
      } catch (e) {
        if (isMounted) {
          setEntitlements({ features: DEFAULT_FEATURES });
          setLoading(false);
        }
      }
    };

    if (!cachedEntitlements) {
      fetchEntitlements();
    } else {
      setLoading(false);
    }

    const onInvalidate = () => {
      cachedEntitlements = null;
      fetchEntitlements();
    };
    window.addEventListener(INVALIDATE_EVENT, onInvalidate);

    return () => {
      isMounted = false;
      window.removeEventListener(INVALIDATE_EVENT, onInvalidate);
    };
  }, []);

  return { entitlements, loading, features: entitlements?.features || DEFAULT_FEATURES };
};

export const useFeature = (featureName) => {
  const { features, loading } = useEntitlements();
  // Fail-closed: solo true explícito del backend habilita el módulo
  const isEnabled = featureName ? features?.[featureName] === true : false;
  
  // Soporte tanto si se usa como booleano directo o destructuración { hasFeature, loading }
  return Object.assign(Boolean(isEnabled), {
    hasFeature: isEnabled,
    loading,
    isEnabled,
  });
};

export default useFeature;
